// Cliente HTTP. El navegador pone Sec-Fetch-Site automáticamente, que es lo que exige el
// CSRF del BFF: no hay que añadir ninguna cabecera a mano.

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error ?? `HTTP ${status}`)
    this.status = status
    this.body = body
  }
}

async function req(path, opts = {}) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: opts.body ? { 'content-type': 'application/json' } : undefined,
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  })
  const text = await res.text()
  const body = text ? JSON.parse(text) : null
  if (!res.ok) throw new ApiError(res.status, body)
  return body
}

export const api = {
  me: () => req('/api/me'),
  projects: () => req('/api/projects'),
  project: (slug) => req(`/api/projects/${slug}`),
  deployments: (slug) => req(`/api/projects/${slug}/deployments`),
  logs: (slug, id) => req(`/api/projects/${slug}/deployments/${id}/logs`),
  deploy: (slug) => req(`/api/projects/${slug}/deploy`, { method: 'POST' }),
  logout: () => req('/auth/logout', { method: 'POST' }),
  audit: (params = '') => req(`/api/audit${params}`),
  members: (slug) => req(`/api/projects/${slug}/members`),
  searchUsers: (slug, q) => req(`/api/projects/${slug}/members/search?q=${encodeURIComponent(q)}`),
  addMember: (slug, userId, nivel) => req(`/api/projects/${slug}/members`, { method: 'POST', body: { userId, nivel } }),
  setMemberLevel: (slug, userId, nivel) =>
    req(`/api/projects/${slug}/members/${userId}`, { method: 'PUT', body: { nivel } }),
  removeMember: (slug, userId) => req(`/api/projects/${slug}/members/${userId}`, { method: 'DELETE' }),
  containers: (slug) => req(`/api/projects/${slug}/containers`),
  metrics: (slug) => req(`/api/projects/${slug}/metrics`),
  overview: (slug) => req(`/api/projects/${slug}/overview`),
  tables: (slug) => req(`/api/projects/${slug}/tables`),
  table: (slug, schema, name) => req(`/api/projects/${slug}/tables/${schema}/${name}`),
  tipos: () => req('/api/ddl/tipos'),
  tablaAvanzado: (slug, schema, name) => req(`/api/projects/${slug}/tables/${schema}/${name}/advanced`),
  ddl: (slug, que, body) => req(`/api/projects/${slug}/ddl/${que}`, { method: 'POST', body }),
  schema: (slug) => req(`/api/projects/${slug}/schema`),
  sqlLimits: () => req('/api/sql/limits'),
  runSql: (slug, sql, deadlineS) =>
    req(`/api/projects/${slug}/sql`, { method: 'POST', body: { sql, deadlineS } }),
  sqlHistory: (slug) => req(`/api/projects/${slug}/sql/history`),
  provisionRules: () => req('/api/provision/rules'),
  checkSlug: (slug) => req(`/api/provision/check?slug=${encodeURIComponent(slug)}`),
  checkRepo: (path) => req(`/api/provision/repo?path=${encodeURIComponent(path)}`),
  createProject: (body) => req('/api/projects', { method: 'POST', body }),
  provisioning: (slug) => req(`/api/projects/${slug}/provisioning`),
  retryProvision: (slug) => req(`/api/projects/${slug}/retry`, { method: 'POST' }),
  deleteProject: (slug, confirm, keepData) =>
    req(`/api/projects/${slug}`, { method: 'DELETE', body: { confirm, keepData } }),
  languages: () => req('/api/functions/languages'),
  functions: (slug) => req(`/api/projects/${slug}/functions`),
  fn: (slug, path) => req(`/api/projects/${slug}/functions/item?path=${encodeURIComponent(path)}`),
  fnHistory: (slug, path) => req(`/api/projects/${slug}/functions/history?path=${encodeURIComponent(path)}`),
  fnConfig: (slug, path) => req(`/api/projects/${slug}/functions/config?path=${encodeURIComponent(path)}`),
  fnToken: (slug, path) => req(`/api/projects/${slug}/functions/token`, { method: 'POST', body: { path } }),
  saveFn: (slug, body) => req(`/api/projects/${slug}/functions`, { method: 'PUT', body }),
  deleteFn: (slug, path) => req(`/api/projects/${slug}/functions`, { method: 'DELETE', body: { path } }),
  // `hash` fija la versión EXACTA a ejecutar: sin él, Windmill resuelve la ruta y justo después
  // de guardar todavía apunta a la versión anterior.
  runFn: (slug, path, args, hash) =>
    req(`/api/projects/${slug}/functions/run`, { method: 'POST', body: { path, args, hash } }),
  previewFn: (slug, body) => req(`/api/projects/${slug}/functions/preview`, { method: 'POST', body }),
  syncFns: (slug) => req(`/api/projects/${slug}/functions/sync`, { method: 'POST' }),
  jobs: (slug, n) => req(`/api/projects/${slug}/jobs${n ? `?n=${n}` : ''}`),
  job: (slug, id) => req(`/api/projects/${slug}/jobs/${id}`),
  cancelJob: (slug, id) => req(`/api/projects/${slug}/jobs/${id}/cancel`, { method: 'POST' }),
  env: (slug) => req(`/api/projects/${slug}/env`),
  saveEnv: (slug, body) => req(`/api/projects/${slug}/env`, { method: 'PUT', body }),
  deleteEnv: (slug, clave) =>
    req(`/api/projects/${slug}/env/${encodeURIComponent(clave)}`, { method: 'DELETE' }),
  previewEnv: (slug, texto) => req(`/api/projects/${slug}/env/preview`, { method: 'POST', body: { texto } }),
  importEnv: (slug, texto, secreto) =>
    req(`/api/projects/${slug}/env/import`, { method: 'POST', body: { texto, secreto } }),
  setPort: (slug, puerto) => req(`/api/projects/${slug}/port`, { method: 'PUT', body: { puerto } }),
  storage: (slug) => req(`/api/projects/${slug}/storage`),
  storageProvision: (slug) => req(`/api/projects/${slug}/storage/provision`, { method: 'POST' }),
  storageList: (slug, prefijo = '') =>
    req(`/api/projects/${slug}/storage/objects?prefijo=${encodeURIComponent(prefijo)}`),
  storageDelete: (slug, clave) =>
    req(`/api/projects/${slug}/storage/objects?clave=${encodeURIComponent(clave)}`, { method: 'DELETE' }),
  // Subida con el cuerpo crudo: el helper req() serializa a JSON y aquí viaja el fichero tal cual.
  storageUpload: async (slug, clave, fichero) => {
    const res = await fetch(`/api/projects/${slug}/storage/objects?clave=${encodeURIComponent(clave)}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/octet-stream' },
      body: fichero,
    })
    const text = await res.text()
    const body = text ? JSON.parse(text) : null
    if (!res.ok) throw new ApiError(res.status, body)
    return body
  },
  quota: (slug) => req(`/api/projects/${slug}/storage/quota`),
  setQuota: (slug, gib) => req(`/api/projects/${slug}/storage/quota`, { method: 'PUT', body: { gib } }),
  deployEnv: (slug) => req(`/api/projects/${slug}/env/deploy`, { method: 'POST' }),
  version: () => req('/api/version'),
  mcpTokens: () => req('/api/admin/mcp-tokens'),
  mcpRevoke: (id) => req(`/api/admin/mcp-tokens/${id}/revoke`, { method: 'POST' }),
  health: () => req('/api/admin/health'),
  sessions: () => req('/api/admin/sessions'),
  revoke: (gitlabUserId, reason) => req('/api/admin/revoke', { method: 'POST', body: { gitlabUserId, reason } }),
}

/** Suscripción SSE a los logs de un build en curso. Devuelve la función de cierre. */
export function streamLogs(slug, deploymentId, { onLine, onStatus, onEnd, onError }) {
  const es = new EventSource(`/api/projects/${slug}/deployments/${deploymentId}/stream`)
  es.addEventListener('line', (e) => onLine?.(JSON.parse(e.data).line))
  es.addEventListener('status', (e) => onStatus?.(JSON.parse(e.data)))
  es.addEventListener('end', (e) => { onEnd?.(JSON.parse(e.data).reason); es.close() })
  es.addEventListener('error', () => { onError?.(); es.close() })
  return () => es.close()
}

/** Suscripción SSE a una ejecución de Windmill. Misma forma que streamLogs. */
export function streamJob(slug, jobId, { onLine, onStatus, onEnd, onError }) {
  const es = new EventSource(`/api/projects/${slug}/jobs/${jobId}/stream`)
  es.addEventListener('line', (e) => onLine?.(JSON.parse(e.data).line))
  es.addEventListener('status', (e) => onStatus?.(JSON.parse(e.data)))
  es.addEventListener('end', (e) => { onEnd?.(JSON.parse(e.data)); es.close() })
  es.addEventListener('error', (e) => {
    // Dos cosas distintas llegan por aquí: el evento `error` que emite el BFF (con data) y el
    // fallo de transporte del propio EventSource (sin data). Solo el segundo es una caída.
    try { onError?.(JSON.parse(e.data).message) } catch { onError?.(null) }
    es.close()
  })
  return () => es.close()
}

/** Logs de la aplicación en ejecución. Misma forma que los de build. */
export function streamRuntimeLogs(slug, { containerId, tail, search }, { onLine, onStatus, onEnd, onError }) {
  const q = new URLSearchParams()
  if (containerId) q.set('containerId', containerId)
  if (tail) q.set('tail', String(tail))
  if (search) q.set('search', search)
  const es = new EventSource(`/api/projects/${slug}/logs/stream?${q}`)
  es.addEventListener('line', (e) => onLine?.(JSON.parse(e.data).line))
  es.addEventListener('status', (e) => onStatus?.(JSON.parse(e.data)))
  es.addEventListener('end', (e) => { onEnd?.(JSON.parse(e.data).motivo); es.close() })
  es.addEventListener('error', (e) => {
    try { onError?.(JSON.parse(e.data).message) } catch { onError?.(null) }
    es.close()
  })
  return () => es.close()
}
